import React from "react";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { STATUS_OPTIONS, PAYMENT_MODE_OPTIONS } from "../constants";
import type { PaymentValidationErrors } from "../types";

interface PaymentScheduleFieldsProps {
  status: string;
  onStatusChange: (val: string) => void;
  dueDate?: Date;
  onDueDateChange: (date?: Date) => void;
  paymentDate?: Date;
  onPaymentDateChange: (date?: Date) => void;
  mode: string;
  onModeChange: (val: string) => void;
  transactionId: string;
  onTransactionIdChange: (val: string) => void;
  notes: string;
  onNotesChange: (val: string) => void;
  errors: PaymentValidationErrors;
}

export const PaymentScheduleFields: React.FC<PaymentScheduleFieldsProps> = ({
  status,
  onStatusChange,
  dueDate,
  onDueDateChange,
  paymentDate,
  onPaymentDateChange,
  mode,
  onModeChange,
  transactionId,
  onTransactionIdChange,
  notes,
  onNotesChange,
  errors,
}) => {
  return (
    <>
      {/* Status */}
      <div>
        <label className="block font-medium mb-1">
          Payment Status <span className="text-destructive">*</span>
        </label>
        <Select value={status} onValueChange={onStatusChange}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((opt) => (
              <SelectItem value={opt.value} key={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.status && (
          <p className="text-destructive text-xs mt-1">{errors.status}</p>
        )}
      </div>

      {/* Due Date */}
      <div>
        <label className="block font-medium mb-1">
          Due Date{" "}
          {status === "pending" && <span className="text-destructive">*</span>}
        </label>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant={"outline"}
              className={`w-full pl-3 text-left font-normal ${
                !dueDate && "text-muted-foreground"
              }`}
              type="button"
            >
              {dueDate ? format(dueDate, "PPP") : <span>Pick a due date</span>}
              <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={dueDate}
              onSelect={onDueDateChange}
              initialFocus
              className="p-3 pointer-events-auto"
            />
          </PopoverContent>
        </Popover>
        {errors.dueDate && (
          <p className="text-destructive text-xs mt-1">{errors.dueDate}</p>
        )}
      </div>

      {/* Payment Date */}
      {status === "paid" && (
        <div>
          <label className="block font-medium mb-1">Payment Date</label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant={"outline"}
                className={`w-full pl-3 text-left font-normal ${
                  !paymentDate && "text-muted-foreground"
                }`}
                type="button"
              >
                {paymentDate ? (
                  format(paymentDate, "PPP")
                ) : (
                  <span>Pick a payment date</span>
                )}
                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={paymentDate}
                onSelect={onPaymentDateChange}
                disabled={(date) => date > new Date()}
                initialFocus
                className="p-3 pointer-events-auto"
              />
            </PopoverContent>
          </Popover>
          {errors.paymentDate && (
            <p className="text-destructive text-xs mt-1">{errors.paymentDate}</p>
          )}
        </div>
      )}

      {/* Payment Mode */}
      <div>
        <label className="block font-medium mb-1">Payment Mode</label>
        <Select value={mode} onValueChange={onModeChange}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PAYMENT_MODE_OPTIONS.map((opt) => (
              <SelectItem value={opt.value} key={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Transaction ID */}
      <div>
        <label className="block font-medium mb-1">Transaction ID</label>
        <Input
          type="text"
          placeholder="Enter transaction ID (optional)"
          value={transactionId}
          onChange={(e) => onTransactionIdChange(e.target.value)}
        />
      </div>

      {/* Notes */}
      <div>
        <label className="block font-medium mb-1">Notes</label>
        <Input
          type="text"
          placeholder="Additional notes (optional)"
          value={notes}
          onChange={(e) => onNotesChange(e.target.value)}
        />
      </div>
    </>
  );
};
